import { JsonPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthAgentRecommendation, GrowthAgentService, parseJsonArray } from './growth-agent.service';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-recommendations',
  imports: [RouterLink, AinPageComponent, AinStateComponent, InfoHintComponent, TableModule, ButtonModule, TagModule],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.agentRecommendations.title')" [description]="t('growth.agentRecommendations.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.agentRecommendations.help.title'" [descriptionKey]="'growth.agentRecommendations.help.description'"
        [bulletKeys]="['growth.agentRecommendations.help.bullets.advisory','growth.agentRecommendations.help.bullets.workflow','growth.agentRecommendations.help.bullets.noExecute']" />
      <div class="toolbar">
        <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
        <a routerLink="/growth/agents/definitions" pButton severity="secondary" [label]="t('growth.agents.console.title')"></a>
        <a routerLink="/growth/marketing/ace-studio" pButton severity="help" [label]="t('growth.ace.studio.title')"></a>
      </div>
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else if (error()) { <ain-state type="error" [title]="t('growth.agentRecommendations.errorLoad')" [message]="error() ?? ''" /> }
      @else {
        <p-table [value]="items()" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.agentRecommendations.columns.title') }}</th>
              <th>{{ t('growth.agentRecommendations.columns.type') }}</th>
              <th>{{ t('growth.agentRecommendations.columns.risk') }}</th>
              <th>{{ t('growth.agentRecommendations.columns.approval') }}</th>
              <th>{{ t('growth.agentRecommendations.columns.actions') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><a [routerLink]="['/growth/agents/recommendations', row.id]">{{ row.title }}</a></td>
              <td>{{ row.recommendationType }}</td>
              <td><p-tag [value]="row.riskBand" [severity]="riskSeverity(row.riskBand)" /></td>
              <td><p-tag [value]="row.approvalState" /></td>
              <td class="actions">
                @if (row.approvalState !== 'Approved' && row.approvalState !== 'Rejected' && row.approvalState !== 'Blocked') {
                  <button pButton type="button" size="small" severity="secondary" [label]="t('growth.agentRecommendations.actions.requestApproval')" (click)="requestApproval(row)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`.toolbar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px; } .actions { display:flex; gap:4px; }`]
})
export class GrowthAgentRecommendationsComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<GrowthAgentRecommendation[]>([]);
  t(key: string): string { return this.i18n.translate(key); }
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.service.listRecommendations().subscribe({
      next: items => { this.items.set(items); this.loading.set(false); },
      error: err => { this.error.set(err?.error?.error ?? err?.message); this.loading.set(false); }
    });
  }
  requestApproval(row: GrowthAgentRecommendation): void {
    this.service.requestApproval(row.id).subscribe({ next: () => this.load(), error: err => this.error.set(err?.error?.error) });
  }
  riskSeverity(band: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (band === 'High' || band === 'Critical') return 'danger';
    if (band === 'Medium') return 'warn';
    return 'secondary';
  }
}

@Component({
  standalone: true,
  selector: 'ain-growth-agent-recommendation-detail',
  imports: [JsonPipe, RouterLink, FormsModule, AinPageComponent, AinStateComponent, InfoHintComponent, ButtonModule, TagModule, InputTextModule],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="item()?.title ?? t('growth.agentRecommendations.detail.title')" [description]="t('growth.agentRecommendations.detail.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.agentRecommendations.detail.help.title'" [descriptionKey]="'growth.agentRecommendations.detail.help.description'" [bulletKeys]="['growth.agentRecommendations.detail.help.bullets.workflow']" />
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else if (!item()) { <ain-state type="empty" [title]="t('common.notFound')" /> }
      @else {
        <div class="card">
          <p><strong>{{ t('growth.agentRecommendations.columns.type') }}:</strong> {{ item()!.recommendationType }}</p>
          <p><strong>{{ t('growth.agentRecommendations.columns.risk') }}:</strong> <p-tag [value]="item()!.riskBand" /></p>
          <p><strong>{{ t('growth.agentRecommendations.columns.approval') }}:</strong> {{ item()!.approvalState }}</p>
          @if (item()!.workflowProcessInstanceId) {
            <p><strong>{{ t('growth.agentRecommendations.fields.workflowProcess') }}:</strong> {{ item()!.workflowProcessInstanceId }}</p>
          }
          <p>{{ item()!.summary }}</p>
          <h4>{{ t('growth.agentRecommendations.fields.citations') }}</h4>
          <ul>@for (c of citations(); track $index) { <li>{{ c | json }}</li> }</ul>
          <h4>{{ t('growth.agentRecommendations.fields.guardrail') }}</h4>
          <pre class="readonly-json">{{ item()!.guardrailResultJson }}</pre>
          <div class="actions">
            @if (item()!.approvalState === 'PendingApproval' || item()!.approvalState === 'Draft') {
              <button pButton [label]="t('growth.agentRecommendations.actions.approve')" (click)="approve()"></button>
              <input pInputText [(ngModel)]="rejectReason" [placeholder]="t('growth.agentRecommendations.fields.rejectReason')" />
              <button pButton severity="danger" [label]="t('growth.agentRecommendations.actions.reject')" (click)="reject()"></button>
            }
            <a routerLink="/growth/agents/recommendations" pButton severity="secondary" [label]="t('common.back')"></a>
          </div>
        </div>
      }
    </ain-page>
  `,
  styles: [`.card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; display:grid; gap:8px; } .actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; } .readonly-json { background:#f9fafb; padding:8px; border-radius:8px; overflow:auto; }`]
})
export class GrowthAgentRecommendationDetailComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);
  readonly loading = signal(true);
  readonly item = signal<GrowthAgentRecommendation | null>(null);
  readonly citations = signal<unknown[]>([]);
  rejectReason = '';
  t(key: string): string { return this.i18n.translate(key); }
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id) { this.loading.set(false); return; }
      this.service.getRecommendation(id).subscribe({
        next: item => { this.item.set(item); this.citations.set(parseJsonArray(item.citationRefsJson)); this.loading.set(false); },
        error: () => this.loading.set(false)
      });
    });
  }
  approve(): void {
    const id = this.item()?.id;
    if (id) this.service.approveRecommendation(id).subscribe({ next: item => this.item.set(item) });
  }
  reject(): void {
    const id = this.item()?.id;
    if (id) this.service.rejectRecommendation(id, this.rejectReason).subscribe({ next: item => this.item.set(item) });
  }
}

@Component({
  standalone: true,
  selector: 'ain-growth-agent-approval-queue',
  imports: [RouterLink, AinPageComponent, InfoHintComponent, TableModule, ButtonModule, TagModule],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.agentRecommendations.approvalQueue.title')" [description]="t('growth.agentRecommendations.approvalQueue.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.agentRecommendations.approvalQueue.help.title'" [descriptionKey]="'growth.agentRecommendations.approvalQueue.help.description'" [bulletKeys]="['growth.agentRecommendations.approvalQueue.help.bullets.workflow']" />
      <p-table [value]="items()" [rows]="10" [paginator]="true">
        <ng-template pTemplate="header">
          <tr><th>{{ t('growth.agentRecommendations.columns.title') }}</th><th>{{ t('growth.agentRecommendations.columns.risk') }}</th><th>{{ t('growth.agentRecommendations.columns.approval') }}</th><th></th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><a [routerLink]="['/growth/agents/recommendations', row.id]">{{ row.title }}</a></td>
            <td><p-tag [value]="row.riskBand" /></td>
            <td>{{ row.approvalState }}</td>
            <td><button pButton size="small" [label]="t('growth.agentRecommendations.actions.approve')" (click)="approve(row.id)"></button></td>
          </tr>
        </ng-template>
      </p-table>
    </ain-page>
  `
})
export class GrowthAgentApprovalQueueComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  readonly items = signal<GrowthAgentRecommendation[]>([]);
  t(key: string): string { return this.i18n.translate(key); }
  ngOnInit(): void { this.load(); }
  load(): void { this.service.listRecommendations('PendingApproval').subscribe(items => this.items.set(items)); }
  approve(id: string): void { this.service.approveRecommendation(id).subscribe(() => this.load()); }
}

@Component({
  standalone: true,
  selector: 'ain-growth-agent-run-detail',
  imports: [JsonPipe, RouterLink, AinPageComponent, AinStateComponent, TagModule, ButtonModule],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.agents.runDetail.title')" [description]="t('growth.agents.runDetail.description')">
      @if (run()) {
        <div class="card">
          <p><strong>{{ t('growth.agents.console.fields.status') }}:</strong> {{ run()!.status }}</p>
          <p><strong>{{ t('growth.agents.console.fields.providerMode') }}:</strong> <p-tag [value]="run()!.providerMode" /></p>
          <p>{{ run()!.outputSummary }}</p>
          <h4>{{ t('growth.agentRecommendations.fields.citations') }}</h4>
          <ul>@for (c of citations(); track $index) { <li>{{ c | json }}</li> }</ul>
          <pre class="readonly-json">{{ run()!.guardrailResultJson }}</pre>
          <a routerLink="/growth/agents/definitions" pButton severity="secondary">{{ t('common.back') }}</a>
        </div>
      } @else { <ain-state type="loading" [title]="t('common.loading')" /> }
    </ain-page>
  `,
  styles: [`.card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; } .readonly-json { background:#f9fafb; padding:8px; }`]
})
export class GrowthAgentRunDetailComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);
  readonly run = signal<import('./growth-agent.service').GrowthAgentRun | null>(null);
  readonly citations = signal<unknown[]>([]);
  t(key: string): string { return this.i18n.translate(key); }
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) this.service.getRun(id).subscribe(r => { this.run.set(r); this.citations.set(parseJsonArray(r.citationRefsJson)); });
    });
  }
}
